import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "../lib/api";
import { useTheme } from "./ThemeContext";
import { setLanguage as setI18nLanguage, getLanguage as getI18nLanguage } from "../i18n";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const { setTheme } = useTheme();

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/me");
      setProfile(data);
      if (data?.theme) setTheme(data.theme);
      // Mirror server-side onboarded into the local safety flag so
      // cold-reload checks (Gate / Welcome) trust either source.
      try {
        if (data?.onboarded) window.localStorage.setItem("ss_onboarded", "1");
      } catch { /* ignore */ }
      // Prefer the server-side language only when the user hasn't already
      // chosen one on this device (avoids overriding a fresh in-session pick).
      if (data?.language && data.language !== getI18nLanguage()) {
        try {
          const stored = window.localStorage.getItem("ss_lang");
          if (!stored) setI18nLanguage(data.language);
        } catch { /* ignore */ }
      }
      return data;
    } catch {
      setProfile(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, [setTheme]);

  useEffect(() => { refresh(); }, [refresh]);

  const signup = async (email, password, display_name) => {
    const { data } = await api.post("/auth/signup", { email, password, display_name });
    setToken(data.token);
    await refresh();
  };
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    await refresh();
  };
  const linkAnon = async (email, password, display_name) => {
    const { data } = await api.post("/auth/link", { email, password, display_name });
    setToken(data.token);
    await refresh();
  };
  const logout = async () => {
    setToken(null);
    // Clear the "onboarded" safety flag so the next account starts fresh.
    try { window.localStorage.removeItem("ss_onboarded"); } catch { /* ignore */ }
    // keep device id so anonymous data is still reachable
    await refresh();
  };

  const saveOnboarding = async (payload) => {
    const { data } = await api.post("/me/onboarding", payload);
    setProfile(data);
    if (data?.theme) setTheme(data.theme);
    return data;
  };

  /** Switch the in-app language and persist it to the user profile. */
  const setLanguage = async (code) => {
    setI18nLanguage(code);
    try {
      const { data } = await api.patch("/me/preferences", { language: code });
      setProfile(data);
    } catch { /* localStorage already updated; we'll retry on next refresh */ }
  };

  return (
    <AuthContext.Provider value={{
      profile, loading, refresh, signup, login, logout, linkAnon, saveOnboarding,
      setLanguage,
      isAuthed: !!getToken(),
      isAnonymous: profile?.is_anonymous ?? true,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
